import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'components/Link';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import s from './EtimesTabs.scss';

const Tabs = props => {
  const {
    active,
    onChangeCallback = () => {},
    tabs: tabsData,
    ga4Type,
    numOfMenuItems,
  } = props;

  const [subSections, setSubSections] = useState(null);
  const [showMoreItems, setShowMoreItems] = useState(false);
  const [highlighterLeft, setHighlighterLeft] = useState(0);
  const [highlighterWidth, setHighlighterWidth] = useState(0);

  const dropdownRef = useRef(null);

  let activeTabRef = null;
  const initalMenuItems = numOfMenuItems || 5;

  const updateHighlighterPosition = () => {
    if (!activeTabRef) {
      return;
    }
    const activeAnchor = activeTabRef;
    const left = activeAnchor && activeAnchor.offsetLeft;
    const width = activeAnchor && activeAnchor.offsetWidth;
    setHighlighterLeft(left);
    setHighlighterWidth(width);
  };

  const setSubSubsectionData = (id, name) => {
    if (!(tabsData instanceof Array && tabsData.length > 0)) {
      return;
    }

    let finalSubSections;
    if (id || (typeof name === 'string' && name.trim())) {
      finalSubSections = tabsData.filter(item => item.name).map(item => {
        const obj = { ...item };
        if (id) {
          obj.active = obj.id === id;
        } else {
          obj.active = obj.name === name;
        }
        return obj;
      });
    } else {
      finalSubSections = [...tabsData];
      const activeItem = finalSubSections.filter(item => item.active);
      if (activeItem.length === 0 && finalSubSections.length > 0) {
        finalSubSections[0].active = true;
      }
    }

    const activeObject = finalSubSections.find(
      section => section.active === true,
    );

    if (activeObject) {
      // Removing the active tab object from its current position
      const index = finalSubSections.indexOf(activeObject);
      if (index + 1 > initalMenuItems) {
        if (index > -1) {
          finalSubSections.splice(index, 1);
        }

        // Inserting the active tab object at the 2nd position
        finalSubSections.splice(1, 0, activeObject);
      }
    }
    setSubSections(finalSubSections);
  };

  const handleClick = (event, dataObj) => {
    event.preventDefault();
    setSubSubsectionData(dataObj.clickedItem?.id, dataObj.clickedItem?.name);
    onChangeCallback(dataObj);
    setShowMoreItems(false);
    if (ga4Type) {
      analyticsWrapper('ga4', {
        event: 'user_click',
        type: ga4Type,
        category: 'tab_change',
        label: dataObj.clickedItem?.name,
      });
    }
  };

  useEffect(() => {
    if (active) {
      setSubSubsectionData(undefined, active);
    } else {
      setSubSubsectionData();
    }
  }, []);

  useEffect(
    () => {
      if (active && tabsData instanceof Array) {
        setSubSubsectionData(undefined, active);
      }
    },
    [active],
  );

  useEffect(
    () => {
      updateHighlighterPosition();
    },
    [subSections, active],
  );

  const getListItem = (section, index) => {
    const link = section.link || section.path || '/';
    if (link) {
      return (
        <Link
          ref={myRef => {
            if (myRef && section.active) {
              activeTabRef = myRef;
            }
          }}
          key={section.name}
          to={link}
          className={section.active ? s.active : ''}
          isspa={!!section.isspa}
          onClick={event =>
            handleClick(event, {
              clickedItem: section,
            })
          }
          index={index}
          aria-label={section.name}
        >
          {section.name}
        </Link>
      );
    }
    return (
      <div
        onClick={event =>
          handleClick(event, {
            clickedItem: section,
          })
        }
        className={section.active ? s.active : ''}
      >
        {section.name}
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMoreItems(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const renderMoreMenu = () => {
    const moreMenuClass = classNames({
      [s.moreMenuItems]: true,
      [s.hide]: !showMoreItems,
    });
    return subSections.length > initalMenuItems ? (
      <div className={s.moreMenuDots} ref={dropdownRef}>
        <span
          onClick={() => {
            setShowMoreItems(!showMoreItems);
          }}
          className={s.dots}
        >
          <i />
          <i />
          <i />
        </span>
        <div className={`${moreMenuClass} ${s.active}`}>
          {subSections
            .slice(initalMenuItems)
            .map((section, index) => getListItem(section, index))}
        </div>
      </div>
    ) : null;
  };

  const renderHighlighter = () => {
    const _styles =
      (highlighterLeft || highlighterLeft === 0) && highlighterWidth
        ? {
            left: highlighterLeft,
            width: highlighterWidth,
            right: 'auto',
          }
        : {};
    return <span className={s.highlighter} style={_styles} />;
  };

  return subSections && Array.isArray(subSections) ? (
    <div className={s.headerTab}>
      <div className={s.tabList}>
        {subSections
          .slice(0, initalMenuItems)
          .map((section, index) => getListItem(section, index))}
        {renderHighlighter()}
      </div>
      {renderMoreMenu(subSections)}
    </div>
  ) : null;
};

Tabs.propTypes = {
  active: PropTypes.string,
  onChangeCallback: PropTypes.func,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      link: PropTypes.string,
      active: PropTypes.bool,
    }),
  ),
  ga4Type: PropTypes.string,
  numOfMenuItems: PropTypes.number,
};

Tabs.defaultProps = {
  active: '',
  onChangeCallback: () => {},
  tabs: undefined,
  ga4Type: '',
  numOfMenuItems: 5,
};

export default Tabs;
